import { Component, AfterViewInit, OnDestroy, OnInit, ChangeDetectorRef, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { JobsCarouselComponent } from '../jobs-carousel/jobs-carousel.component';
import * as L from 'leaflet';

import { JobMapService } from '../../Services/Map-relevented-jobs.service';
import { Subject, takeUntil, fromEvent, debounceTime } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MapSearchRequest } from '../../Models/Map-jobs.model';
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TranslationInitService } from 'app/services/Translate_Services/translation-init.service';
import { MatButtonModule } from '@angular/material/button';
import { TgSettingsService } from 'app/services/TG-Setting_Services/tg-settings.service';
import * as _ from 'lodash';

declare global {
  interface Window { 
    appScope?: any;
    L?: any;
  }
}

@Component({
  selector: 'app-jobs-map',
  standalone: true,
  imports: [CommonModule, DialogModule, ProgressSpinnerModule, JobsCarouselComponent, MatIconModule, MatProgressSpinnerModule, TranslateModule, MatButtonModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './jobs-map.component.html',
  styleUrls: ['./jobs-map.component.scss'],
})
export class JobsMapComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mapContainerDialog') mapContainerDialog?: ElementRef;
  @ViewChild('mapContainerInline') mapContainerInline?: ElementRef;

  public map?: L.Map;
  private markers: L.Marker[] = [];
  private markerGroup?: L.MarkerClusterGroup;
  private userLocationMarker?: L.Marker;
  private userLocationCircle?: L.Circle;
  private resizeObserver: ResizeObserver | null = null;
  private destroy$ = new Subject<void>();
  private mapDestroy$ = new Subject<void>();
  private timeouts: ReturnType<typeof setTimeout>[] = [];
  private mapInterval: any = null;
  private isInitializing = false;
  private pendingLocations: any[] | null = null;
  private currentPage = 1;
  private pageSize = 200;
  public fetchedJobsCount = 0;
  public totalJobsCount = 0;
  public selectedCity: string | null = null;
  public showMap = false;
  public useDialog = false;
  public markerCount = 0;
  public totalLocations = 0;
  public processedCount = 0;
  public isLoading = true;
  public AriaLabel_Jobs: string | null = null;
  public isFetchingMore = false;
  public jobs: any[] = [];
  private jobData: any[] = [];
  public partnerId = Number(document.getElementById("partnerId")?.getAttribute("value"));
  public siteId = Number(document.getElementById("siteId")?.getAttribute("value"));
  contrastcolor: any;
  pageheadercolor: any;
  pageheader: any;
  defaultradius: any;
  tgSettings: any;
  visualbrandingConfig: any;
  private lastClickedItem: any = null;
  private lastClickTime: number = 0;
  private lastFocusedTriggerElement: HTMLElement | null = null;
  // AVT2-1: Track element that triggered dialog open so focus can be restored on close
  private dialogTriggerElement: HTMLElement | null = null;
  // AVT3-3: Shared live-region for screen-reader announcements
  private liveRegion: HTMLElement | null = null;
  private liveRegionAssertive: HTMLElement | null = null;
  private dialogObserver: MutationObserver | null = null;
  public infoPopupVisible = false;
  private infoTriggerElement: HTMLElement | null = null;


  // aria-label removed from <i>: invalid on role="generic" (violation 5/6) and contradicts aria-hidden.
  // The accessible label is set on the parent leaflet-marker-icon wrapper via marker.on('add').
  private readonly markerIcon = L.divIcon({
    html: `<i class="fa fa-map-marker fa-4x custom-fa-marker" aria-hidden="true"></i>`,
    className: 'custom-div-icon',
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -40]
  });

  constructor(private cdr: ChangeDetectorRef, private jobMapService: JobMapService, private translationInit: TranslationInitService, private translateService: TranslateService, private tgsettingService: TgSettingsService, private el: ElementRef) { }

  ngOnInit(): void {
    this.translationInit.getTranslationAsync('AriaLabel_Jobs').then(res => {
      this.AriaLabel_Jobs = res;
      this.cdr.detectChanges();
    });
    this.resetState();
    this.ensureLiveRegion();
    if (!window.appScope) window.appScope = {};
    window.appScope.openJobsMap = (mode: string) => this.openJobsMap(mode);
    this.mapInterval = setInterval(() => {
      if (window.appScope && typeof window.appScope.openJobsMap !== 'function') {
        window.appScope.openJobsMap = (mode: string) => this.openJobsMap(mode);
      }
    }, 1000);

    // Override parent button element role if wrapped in a button
    if (this.el && this.el.nativeElement) {
      const parent = this.el.nativeElement.parentElement;
      if (parent && (parent.tagName === 'BUTTON' || parent.getAttribute('role') === 'button')) {
        parent.setAttribute('role', 'presentation');
        parent.removeAttribute('tabindex');
      }
    }

    // 1. Keydown Listener with Cleanup
    fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          if (this.infoPopupVisible) {
            this.closeInfoPopup();
            return;
          }
          if (this.showMap && this.useDialog) {
            this.showMap = false;
            this.cdr.detectChanges();
            return;
          }
        }
        if ((event.key === 'Enter' || event.key === ' ') && event.target) {
          const target = event.target as HTMLElement;
          
          // Check if target is a Leaflet marker or cluster icon
          const isMarker = target.classList.contains('leaflet-marker-icon') || 
                           target.classList.contains('custom-cluster-icon') ||
                           target.closest('.leaflet-marker-icon') !== null;
                           
          if (isMarker) {
            event.preventDefault();
            this.lastFocusedTriggerElement = target;
            // Get the clickable element (prefer the leaflet-marker-icon wrapper which receives Leaflet events)
            const clickable = target.closest('.leaflet-marker-icon') as HTMLElement || target;
            clickable.click();
          } else if (target.classList.contains('locate-me-btn')) {
            event.preventDefault();
            target.click();
          }
        }
      });

    // 2. Facet Filter Listener with Debouncing and Cleanup
    fromEvent(window, 'facetFilterChanged')
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(400), // Consolidate rapid emissions
      )
      .subscribe((event: any) => {
        // Prevent duplicate execution by ignoring the event if this instance is hidden (e.g. mobile vs desktop views)
      //  console.log('nativeelement',this.el.nativeElement);
        if (this.el && this.el.nativeElement) {
            if (window.getComputedStyle(this.el.nativeElement).display === 'none') {
                return;
            }
        }

        const request = event.detail;
        if (!request) return;
        if (window.appScope) {
          window.appScope.lastSearchRequest = request;
        }
        this.resetState();
        request.BringAllJobs = true;
        request.RequestForMap = true;

        this.jobMapService.getMatchedJobs(request)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              const jobs = response?.Jobs?.Job ?? [];
              this.fetchedJobsCount = response.JobsCount;
              this.onExternalJobsReceived(jobs);
            },
            error: (err) => console.error('Error fetching matched jobs on filter change:', err)
          });
      });
  }

  ngAfterViewInit(): void {
    if (!this.useDialog) {
      this.safeTimeout(() => this.initMap(), 500);
    }
    this.tgSettings = this.tgsettingService.getCurrentSettings()?.tgSettings;
  }
  
  // AVT3-3/5/6: Create (or reuse) a polite ARIA live region for transient announcements, and an assertive one for high-priority announcements.
  private ensureLiveRegion(): void {
    let region = document.getElementById('liveRegionLoginStatus') as HTMLElement;
    if (!region) {
      region = document.createElement('div');
      region.id = 'liveRegionLoginStatus';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      region.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      document.body.appendChild(region);
    }
    this.liveRegion = region;

    let assertiveRegion = document.getElementById('liveRegionAssertive') as HTMLElement;
    if (!assertiveRegion) {
      assertiveRegion = document.createElement('div');
      assertiveRegion.id = 'liveRegionAssertive';
      assertiveRegion.setAttribute('aria-live', 'assertive');
      assertiveRegion.setAttribute('aria-atomic', 'true');
      assertiveRegion.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      document.body.appendChild(assertiveRegion);
    }
    this.liveRegionAssertive = assertiveRegion;
  }

  private announce(message: string, isAssertive = false): void {
    const region = isAssertive ? this.liveRegionAssertive : this.liveRegion;
    if (!region) return;
    region.textContent = message;
    // Clear it after a delay so it's clean for future announcements
    setTimeout(() => {
      if (region && region.textContent === message) {
        region.textContent = '';
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.cleanupMap();
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAllTimeouts();
    if (this.mapInterval) {
      clearInterval(this.mapInterval);
      this.mapInterval = null;
    }
    if (window.appScope?.openJobsMap) delete window.appScope.openJobsMap;
  }

  public closeCarousel(): void {
    this.selectedCity = null;
    this.jobs = [];
    this.cdr.detectChanges();
    if (this.lastFocusedTriggerElement && document.body.contains(this.lastFocusedTriggerElement)) {
      this.lastFocusedTriggerElement.focus();
    }
    this.lastFocusedTriggerElement = null;
  }

  public openJobsMap(mode: string): void {
    // AVT2-1: Remember which element triggered the dialog so we can return focus when it closes
    this.dialogTriggerElement = document.activeElement as HTMLElement;
    this.cleanupMap();
    this.resetState();
    this.isLoading = true;
    this.useDialog = mode === 'dialog' || mode === 'dailog';
    this.showMap = this.useDialog;
    if (this.useDialog) this.pendingLocations = null;
    else this.safeTimeout(() => this.initMap(), 300);
    this.cdr.detectChanges();

    this.safeTimeout(() => {
      const pageheaderbgc = this.getHeaderBackgroundColor();
      if (pageheaderbgc)
      {       
        const normalizedColorbg = this.normalizeColor(pageheaderbgc);
        const brightnessbg = this.getBrightness(normalizedColorbg);
        this.contrastcolor = brightnessbg > 0.17 ? '#000000' : '#ffffff';
        document.documentElement.style.setProperty('--dialogbuttonColor', this.contrastcolor);
      }
    }, 100);

  }

  public onDialogShown(): void {
    const announcement = this.translateService.instant('Search_on_map_dialogue_view_open');
    this.announce(announcement !== 'Search_on_map_dialogue_view_open' ? announcement : 'Search on map dialogue view open');

    // AVT3-1: Focus the dialog title immediately so screen reader announces "map view opened" BEFORE
    // announcing any action buttons (maximize / close). Using 0ms timeout ensures this fires before
    // PrimeNG's internal focus management can move focus to the maximize button.
    this.safeTimeout(() => {
      const headerTitle = document.querySelector('.p-dialog.MapTitle .title') as HTMLElement;
      if (headerTitle) {
        headerTitle.focus();
      }

      // ── Violations 3 & 4: PrimeNG focus-trap spans carry both aria-hidden="true" and
      // tabindex="0". Watch and override dynamically using a MutationObserver to ensure
      // they never become keyboard-focusable while hidden.
      const dialogEl = document.querySelector('.p-dialog.MapTitle') as HTMLElement;
      if (dialogEl) {
        const observer = new MutationObserver(() => {
          const spans = dialogEl.querySelectorAll(
            '[data-pc-section="firstfocusableelement"], [data-pc-section="lastfocusableelement"]'
          );
          spans.forEach(el => {
            if (el.getAttribute('tabindex') !== '-1') {
              el.setAttribute('tabindex', '-1');
            }
          });
        });
        observer.observe(dialogEl, { attributes: true, subtree: true, childList: true });
        this.dialogObserver = observer;

        // Perform initial run
        dialogEl.querySelectorAll(
          '[data-pc-section="firstfocusableelement"], [data-pc-section="lastfocusableelement"]'
        ).forEach(el => el.setAttribute('tabindex', '-1'));
      }
    }, 0);

    this.safeTimeout(() => {
      this.initMap();
      this.safeTimeout(() => {
        const container = this.mapContainerDialog?.nativeElement as HTMLElement | undefined;
        if (container) {
          container.setAttribute('tabindex', '0');
        }

        // ── Violation 1: PrimeNG sets aria-labelledby to its own generated id (pn_id_X_header)
        // which does not exist in the DOM. This is solved declaratively by binding the header title 
        // element's ID to mapDialog.ariaLabelledBy in the template.


        const closeBtn = document.querySelector('.p-dialog.MapTitle .p-dialog-header .p-dialog-close-button') as HTMLElement;
        if (closeBtn) {
          closeBtn.setAttribute('aria-label', 'map view close button');
        }

        // AVT2-2: Maximize/restore announcements are handled by (onMaximize) → onDialogMaximize().
      }, 350);
    }, 300);
  }

  public onDialogHidden(): void {
    const mapTitle = this.translateService.instant('Search_in_Map') || 'Search on Map';
    const closedSuffix = this.translateService.instant('Dialog_closed') || 'dialog closed';
    this.announce(`${mapTitle} ${closedSuffix}`, true);
    // Clean up focus-trap MutationObserver
    if (this.dialogObserver) {
      this.dialogObserver.disconnect();
      this.dialogObserver = null;
    }

    this.cleanupMap();
    this.showMap = false;
    this.useDialog = false;
    this.isLoading = true;
    this.resetState();
    this.cdr.detectChanges();
    this.safeTimeout(() => this.initMap(), 300);

    // AVT2-1: Restore focus immediately to avoid visual delay, running in the next tick (0ms)
    // so the synchronous live-region announcement is registered first.
    const triggerEl = this.dialogTriggerElement;
    this.dialogTriggerElement = null;
    this.safeTimeout(() => {
      const mapTriggerBtn = document.getElementById('searchHeaderRow_BUTTON_1');
      if (mapTriggerBtn) {
        mapTriggerBtn.focus();
      } else if (triggerEl && document.body.contains(triggerEl)) {
        triggerEl.focus();
      }
    }, 0);

  }

  public onDialogMaximize(event: { maximized: boolean }): void {
    const msg = event.maximized
      ? (this.translateService.instant('Dialog_maximized') || 'Dialog maximized')
      : (this.translateService.instant('Dialog_restored') || 'Dialog restored');
    this.announce(msg);
  }

  private onExternalJobsReceived(jobs: any[]): void {
    if (this.useDialog || !jobs?.length){
      this.markerGroup?.clearLayers();
    this.markers = [];
    this.markerCount = 0;
    this.processedCount = 0;
      return;}
    //  console.log(jobs);
    const grouped = this.groupJobsByLocation(jobs);
    if (!grouped.length) return;
    this.pendingLocations = grouped;
    if (!this.map) {
      if (!this.isInitializing) this.safeTimeout(() => this.initMap(), 300);
    } else if (this.markerGroup) {
      this.renderLocationsOnMap(this.pendingLocations);
      this.pendingLocations = null;
    }
  }

  private async ensureMarkerClusterPluginLoaded(): Promise<void> {
    // 1. Ensure Leaflet is on the window object so the plugin can find it
    (window as any).L = L;

    // 2. Check if the plugin is already loaded on the L object
    const leafletAny = L as any;
    if (!leafletAny.markerClusterGroup) {
      try {
        // 3. Dynamically import the plugin after window.L is set
        await import('leaflet.markercluster');
       // console.log('Leaflet MarkerCluster dynamically imported');
      } catch (e) {
        console.error('Failed to dynamically import leaflet.markercluster:', e);
      }
    }

    // 4. Sync between window.L and module L if needed
    const windowAny = window as any;
    if (!leafletAny.markerClusterGroup && windowAny.L?.markerClusterGroup) {
      leafletAny.markerClusterGroup = windowAny.L.markerClusterGroup;
      leafletAny.MarkerClusterGroup = windowAny.L.MarkerClusterGroup;
    }

    // 5. Final validation
    if (!leafletAny.markerClusterGroup) {
      console.error('Leaflet MarkerCluster plugin failed to load');
      throw new Error(
        'Leaflet.markercluster plugin not loaded. ' +
        'Check if the package is installed and accessible.'
      );
    }

   // console.log('Leaflet MarkerCluster plugin ready');
  }

  private async initMap(): Promise<void> {
    if (this.isInitializing) return;
    this.isInitializing = true;
    await this.ensureMarkerClusterPluginLoaded();
    this.cleanupMap();
    const container = this.useDialog
      ? this.mapContainerDialog?.nativeElement
      : this.mapContainerInline?.nativeElement;

    if (!container) {
      console.warn(`Map container not found (useDialog=${this.useDialog}), retrying…`);
      this.isInitializing = false;
      this.safeTimeout(() => this.initMap(), 300);
      return;
    }
    container.setAttribute('role', 'application');
    container.setAttribute('aria-label', 'Interactive jobs map. Use arrow keys to pan, +/- to zoom.');
    container.setAttribute('tabindex', '0');

    try {
      this.map = L.map(container, {
        attributionControl: false,
        maxBounds: [[-90, -180], [90, 180]],
        maxBoundsViscosity: 1.0
      }).setView([16.5, 81.0], 7);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        noWrap: true
      }).addTo(this.map);

      // AVT3-3: Announce zoom-level changes so screen-reader users know the map zoomed in/out.
      let lastZoom = this.map.getZoom();
      this.map.on('zoomend', () => {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        if (currentZoom > lastZoom) {
          const trans = this.translateService.instant('Map_zoomed_in');
          this.announce(trans !== 'Map_zoomed_in' ? trans : 'Map zoomed in');
        } else if (currentZoom < lastZoom) {
          const trans = this.translateService.instant('Map_zoomed_out');
          this.announce(trans !== 'Map_zoomed_out' ? trans : 'Map zoomed out');
        }
        lastZoom = currentZoom;
      });

      this.addLocateMeControl();

      this.map.on('locationfound', (e) => this.onLocationFound(e));
      this.map.on('locationerror', (e) => this.onLocationError(e));

      const L_any = L as any;
      if (L_any.markerClusterGroup && typeof L_any.MarkerClusterGroup === 'function') {
        this.markerGroup = L_any.markerClusterGroup({
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          spiderfyOnMaxZoom: true,
          chunkedLoading: true,
          iconCreateFunction: (cluster:any) => {
            const markers = cluster.getAllChildMarkers();
            let totalJobs = 0;
            let totalLocations = this.totalLocations;
            const clusterTitles: string[] = [];
            markers.forEach((m: any) => {
              totalJobs += m.options?.jobsCount || 0;
              if (m.options?.jobTitles && Array.isArray(m.options.jobTitles)) {
                clusterTitles.push(...m.options.jobTitles);
              }
            });
            const uniqueTitles = Array.from(new Set(clusterTitles));
            const titlesString = uniqueTitles.join(', ');
            const ariaLabel = titlesString
              ? `${totalJobs} jobs in this area`
              : `${totalJobs} jobs in this area. Press Enter to expand.`;
            return L.divIcon({
              html: `
                <div class="custom-cluster-icon">
                  <i class="fa fa-circle cluster-bg" aria-hidden="true"></i>
                  <span class="cluster-text" aria-hidden="true">${totalJobs}</span>
                  <span class="cluster-label" aria-hidden="true">Jobs</span>
                  <span class="sr-only">${ariaLabel}</span>
                </div>
              `,
              className: 'custom-cluster-container',
              iconSize: L.point(60, 60),
              iconAnchor: [30, 30]
            });
          }
        }).addTo(this.map);

        // AVT2-9 / AVT3-5: Do NOT move focus to the map container on cluster click – that causes
        // the screen reader to cycle through every visible cluster marker. Instead, just use the
        // live-region to announce the expansion so the user knows what happened without a focus jump.
        this.markerGroup!.on('clusterclick', (e: any) => {
          const markers = e?.layer?.getAllChildMarkers?.() ?? [];
          if (markers.length) {
            this.announce(`Cluster expanded. ${markers.length} locations visible.`);
            // Focus the first visible child (either sub-cluster or marker) after the zoom transition finishes
            const firstMarker = markers[0];
            if (firstMarker) {
              setTimeout(() => {
                const visibleParent = this.markerGroup?.getVisibleParent(firstMarker);
                if (visibleParent) {
                  const markerEl = visibleParent.getElement();
                  if (markerEl) {
                    markerEl.focus();
                  }
                }
              }, 600);
            }
          }
        });
      } else {
        console.error('Cannot create markerGroup: L.markerClusterGroup is still undefined.');
      }

      if (this.useDialog) {
        this.fetchJobs(1);
      } else if (this.pendingLocations?.length) {
        const locations = this.pendingLocations;
        this.pendingLocations = null;
        this.renderLocationsOnMap(locations);
      } else {
        this.fetchJobsOnMatchedJobs(1);
      }

      this.safeTimeout(() => {
        if (this.isMapActive()) this.map!.invalidateSize();
        this.cdr.detectChanges();
      }, 350);

      this.setupResizeObserver();
    } catch (e) {
      console.error('Error initialising Leaflet map:', e);
      this.isLoading = false;
    } finally {
      this.isInitializing = false;
    }

    this.cdr.detectChanges();
  }

  private cleanupMap(): void {
    this.mapDestroy$.next();
    this.mapDestroy$.complete();
    this.mapDestroy$ = new Subject<void>();
    this.clearAllTimeouts();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.map) {
      try { this.map.remove(); } catch (e) { console.warn('Error removing map:', e); }
      this.map = undefined;
    }
    this.markerGroup = undefined;
    this.markers = [];
  }

  private fetchJobs(page: number): void {
    if (page === 1) {
      this.isLoading = true;
      this.jobData = [];
      this.fetchedJobsCount = 0;
      this.processedCount = 0;
      this.markerCount = 0;
      this.markerGroup?.clearLayers();
      this.markers = [];
    } else {
      this.isFetchingMore = true;
    }

    const hostRequest = window.appScope?.lastSearchRequest ?? {};
    const request: MapSearchRequest = {
      PartnerId: this.partnerId,
      SiteId: this.siteId,
      PageNumber: page,
      PageSize: this.pageSize,
      Keyword: hostRequest.Keyword ?? [],
      Location: hostRequest.Location ?? [],
      FacetFilterFields: hostRequest.FacetFilterFields ?? null,
      CallType: hostRequest.CallType ?? 'SearchButtontype',
      BringAllJobs: true,
      RequestForMap : true
    };

    this.jobMapService.getMapJobs(request as any).pipe(takeUntil(this.mapDestroy$)).subscribe({
      next: (response) => {
        if (!this.isMapActive()) return;
        if (response?.MapLocations) {
          this.jobData = [...this.jobData, ...response.MapLocations];
          this.totalLocations = this.jobData.length;
          this.totalJobsCount = response.TotalJobsCount ?? 0;
          this.fetchedJobsCount += response.ReturnedJobs ?? 0;
          this.addMarkersFromLocations(response.MapLocations);
          if (page === 1) {
            this.isLoading = false;
            this.safeTimeout(() => this.fitMapToMarkers(true), 100);
          }
        }
        this.currentPage = page;
        const hasMore = this.fetchedJobsCount < this.totalJobsCount && (response?.ReturnedJobs ?? 0) > 0;
        if (hasMore) {
          this.fetchJobs(page + 1);
        } else {
          this.isLoading = false;
          this.isFetchingMore = false;
          this.fitMapToMarkers(true);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching map jobs:', err);
        this.isLoading = false;
        this.isFetchingMore = false;
        this.cdr.detectChanges();
      }
    });
  }

  private fetchJobsOnMatchedJobs(page: number): void {

    if (page === 1) {
      this.isLoading = true;
      this.jobData = [];
      this.fetchedJobsCount = 0;
      this.processedCount = 0;
      this.markerCount = 0;
      this.markerGroup?.clearLayers();
      this.markers = [];
    } else {
      this.isFetchingMore = true;
    }

    const hostRequest = window.appScope?.lastSearchRequest ?? {};
    const request: MapSearchRequest = {
      PartnerId: this.partnerId,
      SiteId: this.siteId,
      PageNumber: page,
      PageSize: this.pageSize,
      Keyword: hostRequest.Keyword ?? [],
      Location: hostRequest.Location ?? [],
      FacetFilterFields: hostRequest.FacetFilterFields ?? null,
      CallType: hostRequest.CallType ?? 'SearchButtontype',
      BringAllJobs: true,
      RequestForMap:true
    };

    this.jobMapService.getMatchedJobs(request as any).pipe(takeUntil(this.mapDestroy$)).subscribe({
      next: (response) => {
        if (!this.isMapActive()) return;
        
        const jobs = Array.isArray(response?.Jobs) ? response.Jobs : (response?.Jobs?.Job ?? []);
        
        if (jobs.length > 0) {
          this.jobData = [...this.jobData, ...jobs];
          this.totalLocations = this.jobData.length;
          this.totalJobsCount = response.JobsCount ?? 0;
          this.fetchedJobsCount += jobs.length ?? 0;
          this.addMarkersFromLocations(jobs);
          if (page === 1) {
            this.isLoading = false;
            this.safeTimeout(() => this.fitMapToMarkers(true), 100);
          }
        }
        
        this.currentPage = page;
        const hasMore = this.fetchedJobsCount < this.totalJobsCount && jobs.length > 0;
        if (hasMore) {
          this.fetchJobsOnMatchedJobs(page + 1);
        } else {
          this.isLoading = false;
          this.isFetchingMore = false;
          this.fitMapToMarkers(true);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching map jobs:', err);
        this.isLoading = false;
        this.isFetchingMore = false;
        this.cdr.detectChanges();
      }
    });
  }

  private renderLocationsOnMap(grouped: any[]): void {
    if (!this.map || !this.markerGroup) return;
    this.markerGroup.clearLayers();
    this.markers = [];
    this.markerCount = 0;
    this.processedCount = 0;
    this.jobData = grouped;
    this.totalLocations = grouped.length;
    this.isLoading = false;
    this.addMarkersFromLocations(grouped);
    this.safeTimeout(() => {
      if (this.isMapActive()) this.fitMapToMarkers(true);
    }, 150);
    this.cdr.detectChanges();
  }

 private getJobTitles(item: any): string[] {
  const titles: string[] = [];

  const cleanAndAddTitle = (title: string) => {
    const cleanTitle = title?.replace(/<[^>]*>/g, '').trim();
    if (cleanTitle) {
      titles.push(cleanTitle);
    }
  };

  if (item.JobTitles) {
    const jobs = Array.isArray(item.JobTitles)
      ? item.JobTitles
      : Array.isArray(item.Details)
        ? item.Details
        : [item];

    cleanAndAddTitle(jobs.toString());

    return titles;
  }

  const jobs = Array.isArray(item.Details)
    ? item.Details
    : [item];

  jobs.forEach((job: any) => {
    let rawTitle = job.JobTitle || job.jobtitle || '';

    if (!rawTitle) {
      const questions = Array.isArray(job.Questions)
        ? job.Questions
        : Array.isArray(job.JobTitles)
          ? job.JobTitles
          : [];

      const titleQ = questions.find((q: any) => {
        const name =
          q.QuestionName?.toLowerCase() ||
          q.ClassName?.toLowerCase() ||
          '';

        return name === 'jobtitle' || name === 'jobtitles';
      });

      rawTitle =
        titleQ?.Value ??
        titleQ?.ActualValueFromSolar ??
        titleQ?.JobTitles ??
        titleQ?.JobTitle ??
        '';
    }

    cleanAndAddTitle(rawTitle);
  });

  return titles;
}

  private addMarkersFromLocations(locations: any[]): void {
    if (!this.map || !this.markerGroup) return;

    locations.forEach(item => {
      this.processedCount++;
      const lat = Number(item.Latitude);
      const lng = Number(item.Longitude);
      if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return;

      const jobsCount = parseInt(item.jobs, 10) || item.Details?.length || 0;
      const cityName = item.City ?? `Location (${lat}, ${lng})`;
      const jobTitles = this.getJobTitles(item);
      const uniqueJobTitles = Array.from(new Set(jobTitles));
      const jobTitlesString = uniqueJobTitles.join(', ');
      
      const ariaLabel = jobTitlesString
        ? `${jobsCount} jobs in ${cityName}: ${jobTitlesString}. Press Enter or Space to view.`
        : `${jobsCount} jobs in ${cityName}. Press Enter or Space to view.`;

      // aria-label removed from <i>: invalid on role="generic" + contradicts aria-hidden (violation 5/6).
      // ariaLabel is applied to the leaflet-marker-icon wrapper in marker.on('add').
      const accessibleIcon = L.divIcon({
        html: `<i class="fa fa-map-marker fa-4x custom-fa-marker" aria-hidden="true"></i>`,
                
        className: 'custom-div-icon',
        iconSize: [30, 42],
        iconAnchor: [15, 42],
        popupAnchor: [0, -40]
      });

      const marker = L.marker([lat, lng], {
        icon: accessibleIcon,
        // @ts-ignore
        jobsCount,
        // @ts-ignore
        jobTitles: uniqueJobTitles
      });

      marker.bindTooltip(`
        <div style="text-align:center; padding:4px;">
          <div style="font-weight:bold; font-size:1.1rem; color:#2c3e50;">${jobsCount} ${this.AriaLabel_Jobs}</div>
          <div style="font-size:0.85rem; color:#0d47a1; margin-top:4px;">Click to view</div>
        </div>
      `, { direction: 'top', offset: [0, -14] });

      marker.on('click', () => {
        const el = marker.getElement();
        if (el) {
          this.lastFocusedTriggerElement = el;
        }
        this.onMarkerClick(item);
      });
      marker.on('add', () => {
        const el = marker.getElement();
        if (!el) return;
        el.setAttribute('tabindex', '0');
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', ariaLabel);
        el.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.onMarkerClick(item);
          }
        });
      });

      this.markerGroup!.addLayer(marker);
      this.markers.push(marker);
      this.markerCount++;
    });
  }
//swamy
  private onMarkerClick(item: any): void {
    const now = Date.now();
    if (this.lastClickedItem === item && (now - this.lastClickTime) < 500) {
      return;
    }
    this.lastClickedItem = item;
    this.lastClickTime = now;

    this.selectedCity = item.City ?? `Location (${item.Latitude}, ${item.Longitude})`;
    if (item.Details?.length) {
      this.jobs = item.Details;
      this.cdr.detectChanges();
      // AVT3-6: Announce so screen reader does not read background content on keyboard activation
      this.announce(`${this.jobs.length} jobs loaded for ${this.selectedCity}. Navigating to job list.`);
      this.scrollToCarousel();
      return;
    }
    const hostRequest = window.appScope?.lastSearchRequest ?? {};
    const payload = {
      ...hostRequest,
      PartnerId: this.partnerId ?? 25632,
      SiteId: this.siteId ?? 5649,
      Latitude: Number(item.Latitude),
      Longitude: Number(item.Longitude),
      Keyword: [''],
      listKeyword: [''],
      Location: [''],
      PageNumber: 1,
      PageSize: 10,
      Radius: 0
    };

    this.jobMapService.getMatchedJobs(payload)
      .pipe(takeUntil(this.mapDestroy$))
      .subscribe({
        next: (response) => {
          if (!this.isMapActive()) return;
          this.jobs = response?.Jobs?.Job ?? [];
          this.cdr.detectChanges();
          // AVT3-6: Announce job count so the screen reader doesn't wander into background content
          if (this.jobs.length) {
            this.announce(`${this.jobs.length} jobs loaded for ${this.selectedCity}. Navigating to job list.`);
          }
          this.scrollToCarousel();
        },
        error: (err) => {
          console.error('Error fetching matched jobs:', err);
          this.jobs = [];
          this.cdr.detectChanges();
        }
      });
  }

  private scrollToCarousel(): void {
    this.safeTimeout(() => {
      const carousel = document.querySelector('.carousel-section') as HTMLElement;
      if (carousel) {
        carousel.scrollIntoView({ behavior: 'smooth', block: 'end' });
        carousel.setAttribute('tabindex', '-1');
        // AVT2-9: Focus the first job title in the carousel so screen reader announces the job,
        // not all the map clusters that just became visible after expanding.
        const firstJobTitle = carousel.querySelector<HTMLElement>('[id^="MapJob_"]');
        if (firstJobTitle) {
          firstJobTitle.focus();
        } else {
          carousel.focus();
        }
      }
    }, 200);
  }

  private groupJobsByLocation(items: any[]): any[] {
    const locationsMap = new Map<string, any>();
    for (const item of items) {
      const { lat, lng } = this.extractCoordinates(item, items);
      if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) continue;
      const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
      if (locationsMap.has(key)) {
        const loc = locationsMap.get(key)!;
        loc.jobs = (parseInt(loc.jobs, 10) + 1).toString();
        loc.Details.push(item);
      } else {
        locationsMap.set(key, {
          Latitude: lat.toString(),
          Longitude: lng.toString(),
          jobs: '1',
          Details: [item],
          City: item.City ?? item.city ?? 'Local Location',
        });
      }
    }
    return Array.from(locationsMap.values());
  }

  private extractCoordinates(item: any, allItems: any[]): { lat: number; lng: number } {
    let lat = NaN;
    let lng = NaN;
    const questions: any[] = Array.isArray(item.Questions) ? item.Questions : item?.QuestionName ? allItems : [];
    if (questions.length) {
      const latQ = questions.find(q => q.QuestionName?.toLowerCase() === 'latitude');
      const lngQ = questions.find(q => q.QuestionName?.toLowerCase() === 'longitude');
      lat = parseFloat(latQ?.Value ?? latQ?.ActualValueFromSolar ?? '');
      lng = parseFloat(lngQ?.Value ?? lngQ?.ActualValueFromSolar ?? '');
    }
    if (isNaN(lat) || isNaN(lng)) {
      lat = parseFloat(item.latitude ?? item.Latitude ?? '');
      lng = parseFloat(item.longitude ?? item.Longitude ?? '');
    }
    return { lat, lng };
  }

  private addLocateMeControl(): void {
    const LocateControl = L.Control.extend({
      options: { position: 'topright' },
      onAdd: () => {
        const btn = L.DomUtil.create('button', 'leaflet-bar locate-me-btn lightAccentBkg');
        btn.innerHTML = '<i class="fa fa-crosshairs" aria-hidden="true" style="font-size:18px;"></i>';
        btn.title = 'Show My Location';
        btn.setAttribute('type', 'button');
        btn.setAttribute('tabindex','0');
        btn.setAttribute('aria-label', 'Show my current location on the map');
        btn.style.cssText = 'width:34px;height:34px;line-height:34px;background:white;border:2px solid rgba(0,0,0,0.2);cursor:pointer;border-radius:4px;';
        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.stopPropagation(e);
          this.locateUser();
        });
        L.DomEvent.on(btn, 'keydown', (e: Event) => {
          const ke = e as KeyboardEvent;
          if (ke.key === 'Enter' || ke.key === ' ') {
            ke.preventDefault();
            L.DomEvent.stopPropagation(ke);
            this.locateUser();
          }
        });

        return btn;
      }
    });
    this.map!.addControl(new LocateControl());
  }

  private locateUser(): void {
    if (!this.map) {
      return;
    }
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    this.map.locate({ setView: false, enableHighAccuracy: true });
  }

  private onLocationFound(e: L.LocationEvent): void {
    if (!this.map) return;

    const latlng = e.latlng;
    this.map.setView(latlng, 14, { animate: true });
    if(this.tgSettings == null || this.tgSettings == undefined){
    this.tgSettings = this.tgsettingService.getCurrentSettings()?.tgSettings;
    }
    this.defaultradius = this.tgSettings['ProximityDefaultDistance'] ?? 1000;

    // Remove existing user location elements if they exist
    if (this.userLocationMarker) this.map.removeLayer(this.userLocationMarker);
    if (this.userLocationCircle) this.map.removeLayer(this.userLocationCircle);

    const icon = L.divIcon({
      className: 'my-location-marker',
      html: `<div role="img" aria-label="Your current location" style="background-color:#3498db;width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,0.5);"> </div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    });

    this.userLocationCircle = L.circle(latlng, {
      radius: this.defaultradius,
      color: '#3498db',
      fillColor: '#3498db',
      fillOpacity: 0.15,
      weight: 1
    }).addTo(this.map);
    const locationText = this.translateService.instant('You_are_here') || 'You are here';

    this.userLocationMarker = L.marker(latlng, { icon })
      .addTo(this.map)
      .bindPopup(locationText)
      .openPopup();
  }

  private onLocationError(e: L.ErrorEvent): void {
    console.error('Geolocation error:', e.message);
    alert('Could not determine your location. Please check your permissions.');
  }

  private fitMapToMarkers(animate: boolean): void {
    if (!this.isMapActive() || !this.markers.length) return;
    try {
      this.map!.fitBounds(L.featureGroup(this.markers).getBounds().pad(0.2), { animate });
    } catch (e) {
      console.warn('fitBounds failed:', e);
    }
  }

  private isMapActive(): boolean {
    if (!this.map) return false;
    try {
      const container = this.map.getContainer();
      return !!(container?.parentNode);
    } catch { return false; }
  }

  private setupResizeObserver(): void {
    this.resizeObserver?.disconnect();
    const container = this.useDialog
      ? this.mapContainerDialog?.nativeElement
      : this.mapContainerInline?.nativeElement;
    if (!container) return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isMapActive()) {
        this.map!.invalidateSize();
        //this.fitMapToMarkers(false);
      }
    });
    this.resizeObserver.observe(container);
  }

  private resetState(): void {
    this.totalLocations = 0;
    this.currentPage = 1;
    this.jobData = [];
    this.jobs = [];
    this.selectedCity = null;
    this.processedCount = 0;
    this.markerCount = 0;
    this.fetchedJobsCount = 0;
    this.totalJobsCount = 0;
  }

  private safeTimeout(fn: () => void, delay: number): void {
    const t = setTimeout(() => {
      fn();
      this.timeouts = this.timeouts.filter(x => x !== t);
    }, delay);
    this.timeouts.push(t);
  }

  private clearAllTimeouts(): void {
    this.timeouts.forEach(t => clearTimeout(t));
    this.timeouts = [];
  }

  getFontColor() {


    var brandingConfig = JSON.parse((window as any).$scope.response.BrandingConfiguration);
    var color = '';
    if (brandingConfig.brandingSettings.Responsive_HeaderBackgroundColor != "") {
      color = brandingConfig.brandingSettings.Responsive_HeaderBackgroundColor;
    }
    else {
      var headercolor = document.querySelector('.pageHeader') as HTMLElement;
      var headercolorcss = window.getComputedStyle(headercolor).backgroundColor;
      color = headercolorcss;
    }

    var backgroundBrightness = this.getBrightness(this.normalizeColor(color));

    return backgroundBrightness > 0.179 ? "black" : "white";
  }

  normalizeColor(color: any) {
    if (color.startsWith("#")) {
      let hex = color.replace("#", "");
      if (hex.length === 3) {
        hex = hex.split("").map((c: any) => c + c).join("");
      }
      return [
        parseInt(hex.substring(0, 2), 16),
        parseInt(hex.substring(2, 4), 16),
        parseInt(hex.substring(4, 6), 16)
      ];
    }

    if (color.startsWith("rgb")) {
      return color.match(/\d+/g)!.map(Number);
    }

    const d = document.createElement("div");
    d.style.color = color;
    document.body.appendChild(d);
    const rgb = getComputedStyle(d).color.match(/\d+/g)!.map(Number);
    document.body.removeChild(d);
    return rgb;
  }

  getBrightness(aRGB: any) {
    function toLinear(v: any) {
      v /= 255;
      return v <= 0.03928
        ? v / 12.92
        : Math.pow((v + 0.055) / 1.055, 2.4);
    }

    const R = toLinear(aRGB[0]);
    const G = toLinear(aRGB[1]);
    const B = toLinear(aRGB[2]);

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }
  getHeaderBackgroundColor():any {
    var headerbgc = '';
     this.visualbrandingConfig = this.tgsettingService.getCurrentSettings()?.BrandingConfiguration;
    const pageheader = document.querySelector('.p-dialog-header') as HTMLElement || document.querySelector('.p-dialog') as HTMLElement;
    const pageheadercolor = window.getComputedStyle(pageheader).backgroundColor;

    const rawdata = JSON.parse(unescape(this.visualbrandingConfig));
       _.each(rawdata, (item) => {
         _.each(item, (value, key) => {
           if (key.toLocaleLowerCase().includes("responsive_headerbackgroundcolor")) {
             headerbgc = value.replace(/#Q#/g, '"').replace(/#SQ#/g, "'").replace(/#T#/g, '\t').replace(/#N#/g, '\n').replace(/#BS#/g, '\\').replace(/#@#/g, "'").replace(/Helvetica|'Helvetica Neue'/g, "Arial");  
           }
         });
       });
    return headerbgc != '' ? headerbgc : pageheadercolor;
    
  }

  public toggleInfoPopup(event: Event): void {
    event.stopPropagation();
    if (!this.infoPopupVisible) {
      this.infoTriggerElement = event.currentTarget as HTMLElement;
    }
    this.infoPopupVisible = !this.infoPopupVisible;
    if (this.infoPopupVisible) {
      this.safeTimeout(() => {
        const panel = document.getElementById('mapInfoPopupText');
        if (panel) {
          panel.focus();
        }
      }, 50);
    }
    this.cdr.detectChanges();
  }

  public closeInfoPopup(): void {
    this.infoPopupVisible = false;
    this.cdr.detectChanges();
    if (this.infoTriggerElement && document.body.contains(this.infoTriggerElement)) {
      this.infoTriggerElement.focus();
    }
    this.infoTriggerElement = null;
  }
}
